import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Truck, Plus, User, Phone, Bike, Car, Edit2, Trash2,
  CheckCircle2, Clock, XCircle, DollarSign, Package, MapPin,
  TrendingUp, Loader2, Search, Filter, Calendar
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const fmt = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

interface Driver {
  id: string;
  name: string;
  phone: string | null;
  vehicle_type: string;
  per_delivery_fee: number;
  is_active: boolean;
  created_at: string;
}

interface Delivery {
  id: string;
  order_id: string;
  driver_id: string | null;
  status: string;
  delivery_address: string | null;
  fee_paid: number;
  assigned_at: string | null;
  picked_up_at: string | null;
  delivered_at: string | null;
  notes: string | null;
  created_at: string;
}

interface Order {
  id: string;
  customer_name: string | null;
  customer_phone: string | null;
  total: number;
  status: string;
  order_type: string;
  delivery_address: string | null;
  created_at: string;
}

const vehicleIcons: Record<string, React.ReactNode> = {
  moto: <Bike className="h-4 w-4" />,
  carro: <Car className="h-4 w-4" />,
  bicicleta: <Bike className="h-4 w-4" />,
};

const deliveryStatusConfig: Record<string, { label: string; color: string }> = {
  pending: { label: "Pendente", color: "#3b82f6" },
  assigned: { label: "Atribuído", color: "#f59e0b" },
  picked_up: { label: "Coletado", color: "#8b5cf6" },
  delivered: { label: "Entregue", color: "#22c55e" },
  cancelled: { label: "Cancelado", color: "#ef4444" },
};

const Delivery = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<"entregas" | "entregadores" | "financeiro">("entregas");

  // Drivers
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [driverModal, setDriverModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [driverForm, setDriverForm] = useState({ name: "", phone: "", vehicle_type: "moto", per_delivery_fee: "5" });
  const [savingDriver, setSavingDriver] = useState(false);

  // Deliveries
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [deliveryOrders, setDeliveryOrders] = useState<Order[]>([]);
  const [pendingDeliveryOrders, setPendingDeliveryOrders] = useState<Order[]>([]);
  const [assignModal, setAssignModal] = useState(false);
  const [assigningOrderId, setAssigningOrderId] = useState<string | null>(null);
  const [selectedDriverId, setSelectedDriverId] = useState("");

  // Filters
  const [deliveryFilter, setDeliveryFilter] = useState<"all" | "pending" | "assigned" | "delivered">("all");
  const [dateFilter, setDateFilter] = useState<"today" | "week" | "month" | "all">("today");
  const [driverFilter, setDriverFilter] = useState<string>("all");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { navigate("/login"); return; }

    const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
    let rest: any = null;
    
    if (impersonateId) {
      const { data } = await supabase
        .from("restaurants")
        .select("*")
        .eq("id", impersonateId)
        .limit(1)
        .maybeSingle();
      rest = data;
    } else {
      const { data } = await supabase
        .from("restaurants")
        .select("*")
        .eq("owner_id", user.id)
        .limit(1)
        .maybeSingle();
      rest = data;
    }

    if (!rest) { navigate("/dashboard"); return; }
    setRestaurant(rest);

    const [driversRes, deliveriesRes, ordersRes] = await Promise.all([
      supabase.from("delivery_drivers" as any).select("*").eq("restaurant_id", rest.id).order("name"),
      supabase.from("deliveries" as any).select("*").eq("restaurant_id", rest.id).order("created_at", { ascending: false }),
      supabase.from("orders").select("*").eq("restaurant_id", rest.id).eq("order_type", "delivery").order("created_at", { ascending: false }).limit(200),
    ]);

    setDrivers((driversRes.data as any[]) || []);
    const deliveriesData = (deliveriesRes.data as any[]) || [];
    setDeliveries(deliveriesData);
    const ordersData = (ordersRes.data as any[]) || [];
    setDeliveryOrders(ordersData);

    // Find orders that are delivery type but not yet assigned
    const assignedOrderIds = new Set(deliveriesData.map((d: any) => d.order_id));
    const pending = ordersData.filter((o: any) =>
      !assignedOrderIds.has(o.id) &&
      o.status !== "cancelled" &&
      o.status !== "completed"
    );
    setPendingDeliveryOrders(pending);

    setLoading(false);
  };

  // Driver CRUD
  const openNewDriver = () => {
    setEditingDriver(null);
    setDriverForm({ name: "", phone: "", vehicle_type: "moto", per_delivery_fee: "5" });
    setDriverModal(true);
  };

  const openEditDriver = (d: Driver) => {
    setEditingDriver(d);
    setDriverForm({
      name: d.name,
      phone: d.phone || "",
      vehicle_type: d.vehicle_type,
      per_delivery_fee: String(d.per_delivery_fee),
    });
    setDriverModal(true);
  };

  const saveDriver = async () => {
    if (!restaurant || !driverForm.name.trim()) return;
    setSavingDriver(true);
    const payload = {
      restaurant_id: restaurant.id,
      name: driverForm.name.trim(),
      phone: driverForm.phone.trim() || null,
      vehicle_type: driverForm.vehicle_type,
      per_delivery_fee: parseFloat(driverForm.per_delivery_fee) || 5,
    };

    if (editingDriver) {
      await supabase.from("delivery_drivers" as any).update(payload as any).eq("id", editingDriver.id);
      toast.success("Entregador atualizado!");
    } else {
      await supabase.from("delivery_drivers" as any).insert(payload as any);
      toast.success("Entregador cadastrado!");
    }
    setSavingDriver(false);
    setDriverModal(false);
    loadData();
  };

  const toggleDriverActive = async (d: Driver) => {
    await supabase.from("delivery_drivers" as any).update({ is_active: !d.is_active } as any).eq("id", d.id);
    toast.success(d.is_active ? "Entregador desativado" : "Entregador ativado");
    loadData();
  };

  const deleteDriver = async (d: Driver) => {
    if (!confirm(`Remover ${d.name}?`)) return;
    await supabase.from("delivery_drivers" as any).delete().eq("id", d.id);
    toast.success("Entregador removido");
    loadData();
  };

  // Assign driver to order
  const openAssign = (orderId: string) => {
    setAssigningOrderId(orderId);
    setSelectedDriverId(drivers.filter(d => d.is_active)[0]?.id || "");
    setAssignModal(true);
  };

  const assignDriver = async () => {
    if (!assigningOrderId || !selectedDriverId || !restaurant) return;
    const driver = drivers.find(d => d.id === selectedDriverId);
    if (!driver) return;

    const order = deliveryOrders.find(o => o.id === assigningOrderId);

    await supabase.from("deliveries" as any).insert({
      restaurant_id: restaurant.id,
      order_id: assigningOrderId,
      driver_id: selectedDriverId,
      status: "assigned",
      delivery_address: order?.delivery_address || null,
      fee_paid: driver.per_delivery_fee,
      assigned_at: new Date().toISOString(),
    } as any);

    toast.success(`Entrega atribuída para ${driver.name}`);
    setAssignModal(false);
    loadData();
  };

  const updateDeliveryStatus = async (deliveryId: string, status: string) => {
    const updates: any = { status };
    if (status === "picked_up") updates.picked_up_at = new Date().toISOString();
    if (status === "delivered") updates.delivered_at = new Date().toISOString();

    await supabase.from("deliveries" as any).update(updates).eq("id", deliveryId);
    toast.success("Status atualizado!");
    loadData();
  };

  // Filtered deliveries
  const filteredDeliveries = useMemo(() => {
    let result = deliveries;

    if (deliveryFilter !== "all") {
      result = result.filter(d => d.status === deliveryFilter);
    }

    if (driverFilter !== "all") {
      result = result.filter(d => d.driver_id === driverFilter);
    }

    const now = new Date();
    if (dateFilter === "today") {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      result = result.filter(d => new Date(d.created_at) >= today);
    } else if (dateFilter === "week") {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      result = result.filter(d => new Date(d.created_at) >= weekAgo);
    } else if (dateFilter === "month") {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      result = result.filter(d => new Date(d.created_at) >= monthAgo);
    }

    return result;
  }, [deliveries, deliveryFilter, driverFilter, dateFilter]);

  // Financial summary per driver
  const driverFinancials = useMemo(() => {
    const now = new Date();
    let relevantDeliveries = deliveries;

    if (dateFilter === "today") {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      relevantDeliveries = deliveries.filter(d => new Date(d.created_at) >= today);
    } else if (dateFilter === "week") {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      relevantDeliveries = deliveries.filter(d => new Date(d.created_at) >= weekAgo);
    } else if (dateFilter === "month") {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      relevantDeliveries = deliveries.filter(d => new Date(d.created_at) >= monthAgo);
    }

    return drivers.map(driver => {
      const driverDeliveries = relevantDeliveries.filter(d => d.driver_id === driver.id);
      const completedDeliveries = driverDeliveries.filter(d => d.status === "delivered");
      const totalEarned = completedDeliveries.reduce((s, d) => s + Number(d.fee_paid), 0);
      return {
        driver,
        totalDeliveries: driverDeliveries.length,
        completedDeliveries: completedDeliveries.length,
        pendingDeliveries: driverDeliveries.filter(d => d.status !== "delivered" && d.status !== "cancelled").length,
        totalEarned,
      };
    }).sort((a, b) => b.completedDeliveries - a.completedDeliveries);
  }, [drivers, deliveries, dateFilter]);

  const totalDeliveriesToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return deliveries.filter(d => new Date(d.created_at) >= today).length;
  }, [deliveries]);

  const totalDeliveredToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return deliveries.filter(d => new Date(d.created_at) >= today && d.status === "delivered").length;
  }, [deliveries]);

  const totalCostToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return deliveries
      .filter(d => new Date(d.created_at) >= today && d.status === "delivered")
      .reduce((s, d) => s + Number(d.fee_paid), 0);
  }, [deliveries]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto flex items-center gap-3 p-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Truck className="h-6 w-6 text-primary" />
          <div className="flex-1">
            <h1 className="font-bold text-lg">Delivery</h1>
            <p className="text-xs text-muted-foreground">{restaurant?.name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-4">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-border">
            <CardContent className="p-4 text-center">
              <Package className="h-5 w-5 mx-auto mb-1 text-primary" />
              <p className="text-2xl font-bold">{totalDeliveriesToday}</p>
              <p className="text-xs text-muted-foreground">Entregas hoje</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="h-5 w-5 mx-auto mb-1 text-green-500" />
              <p className="text-2xl font-bold">{totalDeliveredToday}</p>
              <p className="text-xs text-muted-foreground">Entregues</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4 text-center">
              <Clock className="h-5 w-5 mx-auto mb-1 text-amber-500" />
              <p className="text-2xl font-bold">{pendingDeliveryOrders.length}</p>
              <p className="text-xs text-muted-foreground">Sem entregador</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4 text-center">
              <DollarSign className="h-5 w-5 mx-auto mb-1 text-emerald-500" />
              <p className="text-2xl font-bold">{fmt(totalCostToday)}</p>
              <p className="text-xs text-muted-foreground">Custo hoje</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border">
          {(["entregas", "entregadores", "financeiro"] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab === "entregas" ? (
                <span className="flex items-center justify-center gap-1.5"><Package className="h-4 w-4" /> Entregas</span>
              ) : tab === "entregadores" ? (
                <span className="flex items-center justify-center gap-1.5"><User className="h-4 w-4" /> Entregadores</span>
              ) : (
                <span className="flex items-center justify-center gap-1.5"><DollarSign className="h-4 w-4" /> Financeiro</span>
              )}
            </button>
          ))}
        </div>

        {/* === ENTREGAS TAB === */}
        {activeTab === "entregas" && (
          <div className="space-y-4">
            {/* Pending orders (no driver assigned) */}
            {pendingDeliveryOrders.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-amber-500 flex items-center gap-1.5">
                  <Clock className="h-4 w-4" /> Aguardando entregador ({pendingDeliveryOrders.length})
                </h3>
                {pendingDeliveryOrders.map(order => (
                  <Card key={order.id} className="border-amber-500/30 bg-amber-500/5">
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold">#{order.id.slice(0, 6).toUpperCase()}</p>
                          <p className="text-sm text-foreground">{order.customer_name || "Cliente"}</p>
                        </div>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          <MapPin className="h-3 w-3 inline mr-1" />
                          {order.delivery_address || "Sem endereço"}
                        </p>
                      </div>
                      <p className="text-sm font-bold shrink-0">{fmt(Number(order.total))}</p>
                      <Button size="sm" onClick={() => openAssign(order.id)} disabled={drivers.filter(d => d.is_active).length === 0}>
                        <Truck className="h-4 w-4 mr-1" /> Atribuir
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              {(["all", "assigned", "picked_up", "delivered"] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setDeliveryFilter(f === "picked_up" ? "picked_up" : f as any)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    deliveryFilter === f
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {f === "all" ? "Todas" : deliveryStatusConfig[f]?.label || f}
                </button>
              ))}
            </div>

            {/* Deliveries list */}
            {filteredDeliveries.length === 0 ? (
              <Card className="border-border">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Package className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">Nenhuma entrega encontrada</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredDeliveries.map(delivery => {
                  const order = deliveryOrders.find(o => o.id === delivery.order_id);
                  const driver = drivers.find(d => d.id === delivery.driver_id);
                  const status = deliveryStatusConfig[delivery.status] || deliveryStatusConfig.pending;

                  return (
                    <Card key={delivery.id} className="border-border">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                            style={{ background: `${status.color}15`, color: status.color }}
                          >
                            <Truck className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-semibold">#{delivery.order_id.slice(0, 6).toUpperCase()}</p>
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-none" style={{ background: `${status.color}15`, color: status.color }}>
                                {status.label}
                              </Badge>
                              {driver && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  {vehicleIcons[driver.vehicle_type]} {driver.name}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {order?.customer_name || "Cliente"} • {delivery.delivery_address || "Sem endereço"}
                            </p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {new Date(delivery.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                              {delivery.delivered_at && ` • Entregue ${new Date(delivery.delivered_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <p className="text-xs font-medium text-muted-foreground">{fmt(Number(delivery.fee_paid))}</p>
                            {delivery.status === "assigned" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateDeliveryStatus(delivery.id, "picked_up")}>
                                Coletou
                              </Button>
                            )}
                            {delivery.status === "picked_up" && (
                              <Button size="sm" className="h-7 text-xs" onClick={() => updateDeliveryStatus(delivery.id, "delivered")}>
                                Entregue
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* === ENTREGADORES TAB === */}
        {activeTab === "entregadores" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={openNewDriver}>
                <Plus className="h-4 w-4 mr-1" /> Novo entregador
              </Button>
            </div>

            {drivers.length === 0 ? (
              <Card className="border-border">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <User className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">Nenhum entregador cadastrado</p>
                  <p className="text-sm mt-1">Cadastre entregadores para atribuir entregas.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {drivers.map(driver => {
                  const driverDeliveries = deliveries.filter(d => d.driver_id === driver.id);
                  const completedCount = driverDeliveries.filter(d => d.status === "delivered").length;
                  const activeCount = driverDeliveries.filter(d => d.status !== "delivered" && d.status !== "cancelled").length;

                  return (
                    <Card key={driver.id} className={`border-border ${!driver.is_active ? "opacity-50" : ""}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                            {vehicleIcons[driver.vehicle_type] || <Bike className="h-5 w-5" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-sm">{driver.name}</p>
                              <Badge variant={driver.is_active ? "default" : "secondary"} className="text-[10px]">
                                {driver.is_active ? "Ativo" : "Inativo"}
                              </Badge>
                            </div>
                            {driver.phone && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                <Phone className="h-3 w-3" /> {driver.phone}
                              </p>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <DollarSign className="h-3 w-3" /> {fmt(driver.per_delivery_fee)}/entrega
                              </span>
                              <span>{completedCount} entregas</span>
                              {activeCount > 0 && (
                                <span className="text-amber-500">{activeCount} ativa(s)</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDriver(driver)}>
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleDriverActive(driver)}>
                              {driver.is_active ? <XCircle className="h-3.5 w-3.5 text-destructive" /> : <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />}
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteDriver(driver)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* === FINANCEIRO TAB === */}
        {activeTab === "financeiro" && (
          <div className="space-y-4">
            {/* Date filter */}
            <div className="flex flex-wrap gap-2">
              {(["today", "week", "month", "all"] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setDateFilter(f)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    dateFilter === f
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {f === "today" ? "Hoje" : f === "week" ? "Semana" : f === "month" ? "Mês" : "Tudo"}
                </button>
              ))}
            </div>

            {/* Total summary */}
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total a pagar ({dateFilter === "today" ? "hoje" : dateFilter === "week" ? "semana" : dateFilter === "month" ? "mês" : "total"})</p>
                    <p className="text-3xl font-bold text-primary">
                      {fmt(driverFinancials.reduce((s, df) => s + df.totalEarned, 0))}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Entregas concluídas</p>
                    <p className="text-2xl font-bold">{driverFinancials.reduce((s, df) => s + df.completedDeliveries, 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Per-driver breakdown */}
            {driverFinancials.length === 0 ? (
              <Card className="border-border">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">Nenhum entregador cadastrado</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {driverFinancials.map(({ driver, totalDeliveries, completedDeliveries, pendingDeliveries, totalEarned }) => (
                  <Card key={driver.id} className="border-border">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                          {vehicleIcons[driver.vehicle_type] || <Bike className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm">{driver.name}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span>{completedDeliveries} entregue(s)</span>
                            {pendingDeliveries > 0 && <span className="text-amber-500">{pendingDeliveries} pendente(s)</span>}
                            <span>{fmt(driver.per_delivery_fee)}/entrega</span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-lg font-bold text-primary">{fmt(totalEarned)}</p>
                          <p className="text-[10px] text-muted-foreground">a pagar</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* === MODALS === */}

      {/* Driver modal */}
      <Dialog open={driverModal} onOpenChange={setDriverModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingDriver ? "Editar entregador" : "Novo entregador"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground">Nome *</label>
              <input
                className="w-full mt-1 px-3 py-2 rounded-lg bg-muted border border-border text-foreground text-sm outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Nome do entregador"
                value={driverForm.name}
                onChange={e => setDriverForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Telefone</label>
              <input
                className="w-full mt-1 px-3 py-2 rounded-lg bg-muted border border-border text-foreground text-sm outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="(00) 00000-0000"
                value={driverForm.phone}
                onChange={e => setDriverForm(f => ({ ...f, phone: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Veículo</label>
              <div className="flex gap-2 mt-1">
                {["moto", "carro", "bicicleta"].map(v => (
                  <button
                    key={v}
                    onClick={() => setDriverForm(f => ({ ...f, vehicle_type: v }))}
                    className={`flex-1 py-2 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                      driverForm.vehicle_type === v
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground"
                    }`}
                  >
                    {vehicleIcons[v]} {v === "moto" ? "Moto" : v === "carro" ? "Carro" : "Bike"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Valor por entrega (R$)</label>
              <input
                type="number"
                step="0.50"
                min="0"
                className="w-full mt-1 px-3 py-2 rounded-lg bg-muted border border-border text-foreground text-sm outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="5.00"
                value={driverForm.per_delivery_fee}
                onChange={e => setDriverForm(f => ({ ...f, per_delivery_fee: e.target.value }))}
              />
            </div>
            <Button className="w-full" onClick={saveDriver} disabled={savingDriver || !driverForm.name.trim()}>
              {savingDriver ? <Loader2 className="h-4 w-4 animate-spin" /> : editingDriver ? "Salvar alterações" : "Cadastrar entregador"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign driver modal */}
      <Dialog open={assignModal} onOpenChange={setAssignModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Atribuir entregador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Pedido #{assigningOrderId?.slice(0, 6).toUpperCase()}
            </p>
            <div>
              <label className="text-sm font-medium text-foreground">Entregador</label>
              <select
                className="w-full mt-1 px-3 py-2 rounded-lg bg-muted border border-border text-foreground text-sm outline-none"
                value={selectedDriverId}
                onChange={e => setSelectedDriverId(e.target.value)}
              >
                {drivers.filter(d => d.is_active).map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name} — {fmt(d.per_delivery_fee)}/entrega
                  </option>
                ))}
              </select>
            </div>
            <Button className="w-full" onClick={assignDriver} disabled={!selectedDriverId}>
              <Truck className="h-4 w-4 mr-1" /> Confirmar atribuição
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Delivery;